// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useEffect } from 'react';
import { isLoggedIn, getIdToken, logout } from './auth';
import LoginPage from './LoginPage';
import TenantList from './TenantList';
import TenantForm from './TenantForm';
import ReportGenerator from './ReportGenerator';
import './AdminApp.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export default function AdminApp() {
  const [authed, setAuthed] = useState(null); // null = checking, true/false
  const [view, setView] = useState('list'); // list | create | edit
  const [editTenant, setEditTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    isLoggedIn().then(setAuthed);
  }, []);

  async function fetchTenants() {
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load tenants');
      const data = await res.json();
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (authed) fetchTenants();
  }, [authed]);

  function handleLoginSuccess() {
    setAuthed(true);
  }

  function handleLogout() {
    logout();
    setAuthed(false);
  }

  function handleEdit(tenant) {
    setEditTenant(tenant);
    setView('edit');
  }

  function handleCreateNew() {
    setEditTenant(null);
    setView('create');
  }

  function handleSaved() {
    setView('list');
    setEditTenant(null);
    fetchTenants();
  }

  if (authed === null) {
    return <div className="admin-loading">Loading…</div>;
  }

  if (!authed) {
    return <LoginPage onSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="admin">
      <header className="admin__header">
        <div className="admin__header-inner">
          <h1 className="admin__title">Platform Admin</h1>
          <div className="admin__header-actions">
            <span className="admin__user">Logged in</span>
            <button className="admin__logout-btn" onClick={handleLogout}>Log out</button>
          </div>
        </div>
      </header>

      <main className="admin__body">
        {error && <div className="admin__error">{error}</div>}

        {view === 'list' && (
          <>
            <TenantList
              tenants={tenants}
              loading={loading}
              onEdit={handleEdit}
              onCreateNew={handleCreateNew}
              onRefresh={fetchTenants}
            />
            {tenants.length > 0 && (
              <ReportGenerator tenants={tenants} />
            )}
          </>
        )}

        {(view === 'create' || view === 'edit') && (
          <TenantForm
            tenant={editTenant}
            onSaved={handleSaved}
            onCancel={() => { setView('list'); setEditTenant(null); }}
          />
        )}
      </main>
    </div>
  );
}
